import { z } from "zod"

// --- Encoding helpers ---

function encodeParam(data: string): string {
  return btoa(data).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "")
}

function decodeParam(encoded: string): string {
  const base64 = encoded.replace(/-/g, "+").replace(/_/g, "/")
  return atob(base64)
}

// --- Deep link command schema ---

const DeepLinkCommandSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("open_gallery"), slug: z.string() }),
  z.object({ type: z.literal("upload"), galleryId: z.string() }),
  z.object({ type: z.literal("view_photo"), mediaId: z.string() }),
  z.object({ type: z.literal("purchase"), galleryId: z.string() }),
  z.object({ type: z.literal("referral"), userId: z.string() }),
])

export type DeepLinkCommand = z.infer<typeof DeepLinkCommandSchema>

function serializeCommand(cmd: DeepLinkCommand): string {
  switch (cmd.type) {
    case "open_gallery":
      return `g:${cmd.slug}`
    case "upload":
      return `u:${cmd.galleryId}`
    case "view_photo":
      return `p:${cmd.mediaId}`
    case "purchase":
      return `b:${cmd.galleryId}`
    case "referral":
      return `r:${cmd.userId}`
  }
}

function parseCommand(raw: string): DeepLinkCommand | null {
  const [prefix, ...rest] = raw.split(":")
  const value = rest.join(":")
  switch (prefix) {
    case "g":
      return { type: "open_gallery", slug: value }
    case "u":
      return { type: "upload", galleryId: value }
    case "p":
      return { type: "view_photo", mediaId: value }
    case "b":
      return { type: "purchase", galleryId: value }
    case "r":
      return { type: "referral", userId: value }
    default:
      return null
  }
}

export function encodeDeepLink(cmd: DeepLinkCommand): string {
  return encodeParam(serializeCommand(cmd))
}

export function decodeDeepLink(encoded: string): DeepLinkCommand | null {
  try {
    const raw = decodeParam(encoded)
    return parseCommand(raw)
  } catch {
    return null
  }
}

// --- Bot link builders ---

export function createBotLinks(botUsername: string, webAppDomain: string) {
  const base = `https://t.me/${botUsername.replace("@", "")}`
  const appBase = `https://t.me/${botUsername.replace("@", "")}/app`

  return {
    /** Open gallery inside Mini App */
    openGallery(slug: string): string {
      return `${appBase}?startapp=${encodeDeepLink({ type: "open_gallery", slug })}`
    },

    /** Start upload flow for a gallery */
    uploadToGallery(galleryId: string): string {
      return `${base}?start=${encodeDeepLink({ type: "upload", galleryId })}`
    },

    /** View a specific photo */
    viewPhoto(mediaId: string): string {
      return `${appBase}?startapp=${encodeDeepLink({ type: "view_photo", mediaId })}`
    },

    /** Purchase/unlock a gallery */
    purchaseGallery(galleryId: string): string {
      return `${base}?start=${encodeDeepLink({ type: "purchase", galleryId })}`
    },

    /** Referral link */
    referral(userId: string): string {
      return `${base}?start=${encodeDeepLink({ type: "referral", userId })}`
    },

    /** Public web gallery URL */
    galleryWeb(slug: string): string {
      return `${webAppDomain}/g/${slug}`
    },
  }
}

export type BotLinks = ReturnType<typeof createBotLinks>
